import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import {
  Heart, Flame, Moon, Zap,
  Mic, FileText, BookOpen, RefreshCw, Loader2,
  ChevronRight, Utensils, Star,
  Book, Film, Music, Sparkles, Archive, Feather, Clock, TrendingUp
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════════════════════════
// LIQUID GLASS COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

const LiquidGlassCard = ({ children, className = "", onClick, accent, testId, size = "default" }) => {
  const sizeClasses = { small: "p-4", default: "p-5", large: "p-6" };
  const accentStyles = {
    green: "from-emerald-500/5 to-transparent",
    red: "from-red-500/5 to-transparent",
    none: "from-white/[0.03] to-transparent"
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={onClick ? { scale: 1.02, y: -4 } : {}}
      whileTap={onClick ? { scale: 0.98 } : {}}
      transition={{ type: "spring", stiffness: 400, damping: 25 }}
      onClick={onClick}
      data-testid={testId}
      className={`
        relative overflow-hidden rounded-[24px]
        bg-white/[0.04] backdrop-blur-2xl
        border border-white/[0.08]
        shadow-[0_8px_32px_rgba(0,0,0,0.4)]
        ${onClick ? 'cursor-pointer' : ''} ${sizeClasses[size]} ${className}
      `}
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${accentStyles[accent] || accentStyles.none} pointer-events-none`} />
      <div className="absolute inset-0 bg-gradient-to-br from-white/[0.06] via-transparent to-transparent pointer-events-none rounded-[24px]" />
      <div className="relative z-10">{children}</div>
    </motion.div>
  );
};

const GlowRing = ({ value, max, color, glowColor, size = 72, strokeWidth = 8 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const progress = Math.min(value / max, 1);
  const offset = circumference - progress * circumference;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <div className="absolute inset-0 rounded-full blur-xl opacity-30" style={{ backgroundColor: glowColor }} />
      <svg width={size} height={size} className="transform -rotate-90 relative z-10">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={strokeWidth} />
        <circle
          cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={strokeWidth}
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
          className="transition-all duration-1000 ease-out"
          style={{ filter: `drop-shadow(0 0 6px ${glowColor})` }}
        />
      </svg>
    </div>
  );
};

const ActionTile = ({ icon: Icon, label, variant = "green", onClick, testId }) => {
  const variants = {
    green: "from-emerald-500 to-emerald-600",
    neon: "from-[#00FF88] to-emerald-500",
    red: "from-red-500 to-red-600",
    dark: "from-zinc-700 to-zinc-800",
  };

  return (
    <motion.button
      whileHover={{ scale: 1.06, y: -3 }}
      whileTap={{ scale: 0.95 }}
      transition={{ type: "spring", stiffness: 400, damping: 20 }}
      onClick={onClick}
      data-testid={testId}
      className={`
        relative flex flex-col items-center justify-center gap-2
        w-full aspect-square rounded-[20px]
        bg-gradient-to-br ${variants[variant]}
        shadow-[0_6px_20px_rgba(0,0,0,0.3)] overflow-hidden
      `}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
      <Icon className="w-6 h-6 text-white relative z-10 drop-shadow-md" strokeWidth={2} />
      <span className="text-[10px] font-semibold text-white/90 relative z-10 uppercase tracking-wide">{label}</span>
    </motion.button>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN DASHBOARD
// ═══════════════════════════════════════════════════════════════════════════════

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [health, setHealth] = useState(null);
  const [prompt, setPrompt] = useState(null);
  const [recentNotes, setRecentNotes] = useState([]);
  const [stats, setStats] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const [showLogModal, setShowLogModal] = useState(false);
  const [logType, setLogType] = useState(null);
  const [logForm, setLogForm] = useState({ title: "", details: "", rating: 0 });
  const [loggingLoading, setLoggingLoading] = useState(false);
  const [showHealthModal, setShowHealthModal] = useState(false);
  const [healthForm, setHealthForm] = useState({ steps: "", calories_burned: "", heart_rate: "" });

  useEffect(() => { loadDashboard(); }, []);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const [healthRes, promptRes, journalRes, statsRes, timelineRes] = await Promise.all([
        api.get("/oura/today").catch(() => ({ data: {} })),
        api.get("/prompts/daily").catch(() => ({ data: null })),
        api.get("/journal?limit=3").catch(() => ({ data: { entries: [] } })),
        api.get("/dashboard/stats").catch(() => ({ data: {} })),
        api.get("/timeline").catch(() => ({ data: null })),
      ]);
      setHealth(healthRes.data);
      setPrompt(promptRes.data);
      setRecentNotes(journalRes.data.entries || []);
      setStats(statsRes.data);
      setTimeline(timelineRes.data);
    } catch (err) {
      console.error("Dashboard load error:", err);
    } finally {
      setLoading(false);
    }
  };

  const syncOura = async () => {
    setSyncing(true);
    try {
      await api.post("/oura/sync");
      const res = await api.get("/oura/today");
      setHealth(res.data);
    } catch (err) { console.error("Sync error:", err); }
    finally { setSyncing(false); }
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const res = await api.post("/timeline/regenerate");
      setTimeline(res.data);
      toast.success("Your story has been refreshed!");
    } catch {
      toast.error("Failed to regenerate story");
    } finally {
      setRegenerating(false);
    }
  };

  const openLogModal = (type) => {
    setLogType(type);
    setLogForm({ title: "", details: "", rating: 0 });
    setShowLogModal(true);
  };

  const handleSubmitLog = async () => {
    if (!logForm.title.trim()) return;
    setLoggingLoading(true);
    try {
      await api.post("/quick-log", { log_type: logType, title: logForm.title, details: logForm.details, rating: logForm.rating || null, date: new Date().toISOString().split("T")[0] });
      setShowLogModal(false);
    } catch (err) { console.error("Quick log error:", err); }
    finally { setLoggingLoading(false); }
  };

  const handleUpdateHealth = async () => {
    try {
      const updates = {};
      if (healthForm.steps) updates.steps = parseInt(healthForm.steps);
      if (healthForm.calories_burned) updates.calories_burned = parseInt(healthForm.calories_burned);
      if (healthForm.heart_rate) updates.heart_rate = parseInt(healthForm.heart_rate);
      await api.post("/oura/manual", updates);
      setShowHealthModal(false);
      loadDashboard();
    } catch (err) { console.error("Health update error:", err); }
  };

  const logTypeConfig = {
    book: { icon: Book, label: "Book", placeholder: "Enter book title", emoji: "\u{1F4DA}" },
    movie: { icon: Film, label: "Movie", placeholder: "Enter movie title", emoji: "\u{1F3AC}" },
    concert: { icon: Music, label: "Concert", placeholder: "Enter artist/event name", emoji: "\u{1F3B5}" }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 18) return "Good Afternoon";
    return "Good Evening";
  };

  const book = timeline?.book || {};
  const hasStory = timeline?.narrative && timeline.items?.length > 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#00FF88] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black p-4 md:p-6 lg:p-8" data-testid="dashboard-page">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight" style={{ fontFamily: 'Manrope' }}>
          {getGreeting()}, <span className="text-[#00FF88]">{user?.name?.split(' ')[0] || 'there'}</span>
        </h1>
        <p className="text-white/30 mt-2 text-sm">
          {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
        </p>
      </motion.div>

      {/* Stats Row */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="grid grid-cols-4 gap-3 mb-6"
      >
        {[
          { label: "Vault", count: stats?.vault_entries || 0, icon: Archive, path: "/vault" },
          { label: "Journal", count: stats?.journal_entries || 0, icon: BookOpen, path: "/journal" },
          { label: "Legacy", count: stats?.legacy_messages || 0, icon: Heart, path: "/legacy-messages" },
          { label: "Chapters", count: book.total_chapters || 0, icon: Book, path: "/profile" },
        ].map((s, i) => (
          <motion.div
            key={i}
            whileHover={{ scale: 1.04, y: -2 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => navigate(s.path)}
            className="bg-white/[0.04] backdrop-blur-xl border border-white/[0.08] rounded-[16px] p-4 text-center cursor-pointer hover:border-white/[0.14] transition-colors"
            data-testid={`stat-${s.label.toLowerCase()}`}
          >
            <s.icon className="w-4 h-4 text-slate-500 mx-auto mb-1.5" strokeWidth={1.5} />
            <p className="text-xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>{s.count}</p>
            <p className="text-[10px] text-slate-500">{s.label}</p>
          </motion.div>
        ))}
      </motion.div>

      {/* Bento Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 auto-rows-min">

        {/* ═══ YOUR BOOK WIDGET ═══ */}
        <LiquidGlassCard
          className="col-span-2 md:col-span-4 lg:col-span-4"
          testId="widget-book"
          accent="green"
          size="large"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#00FF88] to-emerald-600 flex items-center justify-center shadow-lg shadow-[#00FF88]/20">
                <Book className="w-5 h-5 text-black" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white">Your Life Story</h3>
                <p className="text-[10px] text-white/30">{book.total_chapters || 0} chapters &middot; {book.words_written || 0} words</p>
              </div>
            </div>
            <Button
              onClick={(e) => { e.stopPropagation(); handleRegenerate(); }}
              disabled={regenerating}
              variant="ghost"
              size="sm"
              className="text-white/50 hover:text-white hover:bg-white/10 rounded-xl"
              data-testid="refresh-story-btn"
            >
              <RefreshCw className={`w-4 h-4 ${regenerating ? "animate-spin" : ""}`} />
            </Button>
          </div>

          {hasStory ? (
            <div>
              {/* Narrative with optional cover image */}
              <div className="relative rounded-2xl overflow-hidden mb-4">
                {timeline.narrative_image && (
                  <div className="absolute inset-0">
                    <img
                      src={`${BACKEND_URL}/api/vault/files/${timeline.narrative_image}`}
                      alt="Your narrative"
                      className="w-full h-full object-cover opacity-20"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/80 to-transparent" />
                  </div>
                )}
                <div className={`relative p-5 md:p-6 ${!timeline.narrative_image ? 'bg-gradient-to-br from-[#00FF88]/5 via-transparent to-transparent rounded-2xl border border-[#00FF88]/10' : ''}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <Flame className="w-3.5 h-3.5 text-[#00FF88]" />
                    <span className="text-[10px] tracking-[0.2em] uppercase text-[#00FF88] font-medium">Your Narrative</span>
                  </div>
                  <p className="text-sm md:text-base text-slate-200 leading-relaxed italic" style={{ fontFamily: "Manrope" }}>
                    &ldquo;{timeline.narrative}&rdquo;
                  </p>
                </div>
              </div>

              {/* Book Progress Bar */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] text-white/30 uppercase tracking-wider">Book Progress</span>
                  <span className="text-xs text-[#00FF88] font-medium">{book.completion_percentage || 0}%</span>
                </div>
                <Progress value={book.completion_percentage || 0} className="h-2 bg-white/5" />
              </div>

              {/* Chapter Pills */}
              {book.chapters && book.chapters.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {book.chapters.slice(0, 6).map((ch) => (
                    <div key={ch.month_key} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/[0.08]">
                      <span className="text-[10px] font-bold text-[#00FF88]">{ch.number}</span>
                      <span className="text-[10px] text-slate-400">{formatMonthKey(ch.month_key)}</span>
                    </div>
                  ))}
                  {book.chapters.length > 6 && (
                    <div className="px-3 py-1.5 rounded-full bg-white/5 border border-white/[0.08] text-[10px] text-slate-500">
                      +{book.chapters.length - 6} more
                    </div>
                  )}
                </div>
              )}

              {/* CTA */}
              <div className="flex items-start gap-2 bg-[#00FF88]/5 border border-[#00FF88]/10 rounded-xl p-3">
                <Feather className="w-4 h-4 text-[#00FF88] shrink-0 mt-0.5" />
                <p className="text-xs text-slate-400">
                  <span className="text-[#00FF88] font-medium">Keep writing!</span> Every journal entry, prompt response, and memory adds a new page to your book.
                </p>
              </div>
            </div>
          ) : (
            /* Empty Book State */
            <div
              className="text-center py-8 cursor-pointer"
              onClick={() => navigate('/journal')}
            >
              <div className="w-14 h-14 rounded-2xl bg-[#00FF88]/10 flex items-center justify-center mx-auto mb-4">
                <Book className="w-7 h-7 text-[#00FF88]" />
              </div>
              <p className="text-base text-white mb-1" style={{ fontFamily: "Manrope" }}>Your book starts here</p>
              <p className="text-xs text-slate-500 max-w-xs mx-auto mb-4">
                Upload photos, write journal entries, and respond to daily prompts to begin writing your life story.
              </p>
              <span className="inline-flex items-center gap-1.5 text-xs text-[#00FF88] font-medium">
                Start Writing <ChevronRight className="w-3.5 h-3.5" />
              </span>
            </div>
          )}
        </LiquidGlassCard>

        {/* ═══ HEALTH WIDGET ═══ */}
        <LiquidGlassCard className="col-span-2" testId="widget-health" accent="green">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg shadow-red-500/20">
                <Heart className="w-4 h-4 text-white" />
              </div>
              <span className="text-sm font-semibold text-white">Health</span>
            </div>
            {health?.oura_connected ? (
              <button onClick={syncOura} disabled={syncing} className="p-2 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                <RefreshCw className={`w-4 h-4 text-white/50 ${syncing ? 'animate-spin' : ''}`} />
              </button>
            ) : (
              <button onClick={() => setShowHealthModal(true)} className="text-xs text-[#00FF88] font-medium">+ Add</button>
            )}
          </div>

          <div className="flex justify-around items-center mb-4">
            <div className="relative flex flex-col items-center">
              <GlowRing value={health?.heart_rate || 0} max={120} color="#FF3B30" glowColor="#FF3B30" size={72} />
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-lg font-bold text-white">{health?.heart_rate || '--'}</span>
                <span className="text-[9px] text-white/40 font-medium">BPM</span>
              </div>
            </div>
            <div className="relative flex flex-col items-center">
              <GlowRing value={health?.steps || 0} max={10000} color="#00FF88" glowColor="#00FF88" size={72} />
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-lg font-bold text-white">{health?.steps ? Math.round(health.steps / 1000) + 'k' : '--'}</span>
                <span className="text-[9px] text-white/40 font-medium">Steps</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="flex items-center gap-2 p-2.5 rounded-xl bg-white/5">
              <Flame className="w-4 h-4 text-red-400" />
              <div>
                <p className="text-[9px] text-white/30 uppercase">Burned</p>
                <p className="text-sm font-bold text-white">{health?.calories_burned || 0}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 p-2.5 rounded-xl bg-white/5">
              <Utensils className="w-4 h-4 text-emerald-400" />
              <div>
                <p className="text-[9px] text-white/30 uppercase">Intake</p>
                <p className="text-sm font-bold text-white">{health?.calories_intake || 0}</p>
              </div>
            </div>
          </div>

          {(health?.sleep_score || health?.readiness_score) && (
            <div className="mt-3 pt-3 border-t border-white/5 flex justify-around">
              {health?.sleep_score && (
                <div className="text-center">
                  <Moon className="w-4 h-4 text-emerald-400 mx-auto mb-1" />
                  <p className="text-base font-bold text-white">{health.sleep_score}</p>
                  <p className="text-[9px] text-white/30">Sleep</p>
                </div>
              )}
              {health?.readiness_score && (
                <div className="text-center">
                  <Zap className="w-4 h-4 text-[#00FF88] mx-auto mb-1" />
                  <p className="text-base font-bold text-white">{health.readiness_score}</p>
                  <p className="text-[9px] text-white/30">Ready</p>
                </div>
              )}
            </div>
          )}

          {!health?.oura_connected && (
            <button onClick={() => navigate('/connections')} className="w-full mt-3 py-2.5 rounded-xl bg-[#00FF88]/10 border border-[#00FF88]/20 text-[#00FF88] text-xs font-semibold hover:bg-[#00FF88]/20 transition-all">
              Connect Oura Ring
            </button>
          )}
        </LiquidGlassCard>

        {/* ═══ PROMPT OF THE DAY ═══ */}
        <LiquidGlassCard className="col-span-2 md:col-span-2" onClick={() => navigate('/prompts')} testId="widget-prompt" accent="green">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#00FF88] to-emerald-500 flex items-center justify-center shadow-lg shadow-[#00FF88]/20">
              <Sparkles className="w-5 h-5 text-black" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">Prompt of the Day</h3>
              <p className="text-[10px] text-white/30">Tap to respond</p>
            </div>
          </div>

          {prompt ? (
            <div className="space-y-4">
              <p className="text-base text-white leading-relaxed font-medium">&ldquo;{prompt.prompt || prompt.prompt_text}&rdquo;</p>
              <div className="flex items-center justify-between">
                {prompt.responded ? (
                  <span className="text-xs text-[#00FF88] flex items-center gap-1.5 bg-[#00FF88]/10 px-3 py-1.5 rounded-full">
                    <span className="w-2 h-2 rounded-full bg-[#00FF88]" /> Answered
                  </span>
                ) : (
                  <span className="text-xs text-white/50 bg-white/5 px-3 py-1.5 rounded-full">Share your thoughts</span>
                )}
                <ChevronRight className="w-5 h-5 text-white/20" />
              </div>
            </div>
          ) : (
            <div className="animate-pulse space-y-3">
              <div className="h-4 bg-white/10 rounded-lg w-3/4" />
              <div className="h-4 bg-white/10 rounded-lg w-1/2" />
            </div>
          )}
        </LiquidGlassCard>

        {/* ═══ RECENT NOTES ═══ */}
        <LiquidGlassCard className="col-span-2" onClick={() => navigate('/journal')} testId="widget-notes">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
                <FileText className="w-4 h-4 text-white" />
              </div>
              <span className="text-sm font-semibold text-white">Notes</span>
            </div>
            <span className="text-xs text-white/20">{recentNotes.length}</span>
          </div>

          {recentNotes.length > 0 ? (
            <div className="space-y-2">
              {recentNotes.slice(0, 2).map((note, idx) => (
                <div key={note.entry_id || idx} className="p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                  <p className="text-sm font-medium text-white truncate">{note.title}</p>
                  <p className="text-xs text-white/30 mt-0.5 line-clamp-1">{note.content?.replace(/<[^>]*>/g, '').slice(0, 50)}...</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4"><p className="text-xs text-white/30">No notes yet</p></div>
          )}
        </LiquidGlassCard>

        {/* ═══ QUICK ACTIONS ═══ */}
        <div className="col-span-2 md:col-span-4 lg:col-span-2">
          <p className="text-xs text-white/20 uppercase tracking-widest mb-3 font-semibold">Quick Actions</p>
          <div className="grid grid-cols-4 gap-3">
            <ActionTile icon={Mic} label="Voice" variant="neon" onClick={() => navigate('/voice-chat')} testId="quick-action-voice" />
            <ActionTile icon={FileText} label="Note" variant="green" onClick={() => navigate('/journal')} testId="quick-action-note" />
            <ActionTile icon={BookOpen} label="Journal" variant="green" onClick={() => navigate('/journal')} testId="quick-action-journal" />
            <ActionTile icon={Utensils} label="Meal" variant="red" onClick={() => navigate('/meals')} testId="quick-action-meal" />
          </div>
          <div className="grid grid-cols-3 gap-3 mt-3">
            <ActionTile icon={Book} label="Book" variant="dark" onClick={() => openLogModal('book')} testId="quick-action-log-book" />
            <ActionTile icon={Film} label="Movie" variant="dark" onClick={() => openLogModal('movie')} testId="quick-action-log-movie" />
            <ActionTile icon={Music} label="Concert" variant="dark" onClick={() => openLogModal('concert')} testId="quick-action-log-concert" />
          </div>
        </div>

        {/* ═══ VAULT STATS ═══ */}
        <LiquidGlassCard className="col-span-2" onClick={() => navigate('/vault')} testId="widget-stats" accent="green">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm font-semibold text-white">Your Vault</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-2xl bg-white/5 text-center">
              <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>{stats?.vault_entries || 0}</p>
              <p className="text-[10px] text-white/30 uppercase tracking-wider mt-1">Memories</p>
            </div>
            <div className="p-3 rounded-2xl bg-white/5 text-center">
              <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>{stats?.journal_entries || 0}</p>
              <p className="text-[10px] text-white/30 uppercase tracking-wider mt-1">Journal</p>
            </div>
            <div className="p-3 rounded-2xl bg-white/5 text-center">
              <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>{stats?.prompt_responses || 0}</p>
              <p className="text-[10px] text-white/30 uppercase tracking-wider mt-1">Prompts</p>
            </div>
            <div className="p-3 rounded-2xl bg-white/5 text-center">
              <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>{stats?.legacy_messages || 0}</p>
              <p className="text-[10px] text-white/30 uppercase tracking-wider mt-1">Legacy</p>
            </div>
          </div>
        </LiquidGlassCard>
      </div>

      {/* Quick Log Modal */}
      <Dialog open={showLogModal} onOpenChange={setShowLogModal}>
        <DialogContent className="bg-[#0A0A0A]/95 backdrop-blur-2xl border-white/10 text-white max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3" style={{ fontFamily: 'Manrope' }}>
              {logType && logTypeConfig[logType] && (
                <><span className="text-2xl">{logTypeConfig[logType].emoji}</span><span>Log {logTypeConfig[logType].label}</span></>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Input placeholder={logType ? logTypeConfig[logType]?.placeholder : "Enter title"} value={logForm.title} onChange={(e) => setLogForm({ ...logForm, title: e.target.value })} data-testid="quick-log-title" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-2xl h-14" />
            <Input placeholder="Details or notes (optional)" value={logForm.details} onChange={(e) => setLogForm({ ...logForm, details: e.target.value })} data-testid="quick-log-details" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-2xl h-14" />
            <div>
              <p className="text-xs text-white/30 mb-2 uppercase tracking-wider">Rating</p>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button key={star} type="button" onClick={() => setLogForm({ ...logForm, rating: logForm.rating === star ? 0 : star })} data-testid={`rating-star-${star}`} className="p-1 transition-transform hover:scale-125">
                    <Star className={`w-7 h-7 ${star <= logForm.rating ? "text-[#00FF88] fill-[#00FF88] drop-shadow-[0_0_8px_rgba(0,255,136,0.6)]" : "text-white/20"}`} />
                  </button>
                ))}
              </div>
            </div>
            <Button onClick={handleSubmitLog} disabled={loggingLoading || !logForm.title.trim()} data-testid="quick-log-submit" className="w-full bg-[#00FF88] hover:bg-[#00CC6A] text-black rounded-2xl h-14 font-semibold text-base shadow-lg shadow-[#00FF88]/20">
              {loggingLoading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Saving...</> : "Log It"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Health Modal */}
      <Dialog open={showHealthModal} onOpenChange={setShowHealthModal}>
        <DialogContent className="bg-[#0A0A0A]/95 backdrop-blur-2xl border-white/10 text-white max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3" style={{ fontFamily: 'Manrope' }}>
              <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center">
                <Heart className="w-5 h-5 text-white" />
              </div>
              Log Health Data
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-xs text-white/30 mb-1 block uppercase tracking-wider">Steps</label>
              <Input type="number" placeholder="e.g., 8500" value={healthForm.steps} onChange={(e) => setHealthForm({ ...healthForm, steps: e.target.value })} className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-2xl h-14" />
            </div>
            <div>
              <label className="text-xs text-white/30 mb-1 block uppercase tracking-wider">Calories Burned</label>
              <Input type="number" placeholder="e.g., 450" value={healthForm.calories_burned} onChange={(e) => setHealthForm({ ...healthForm, calories_burned: e.target.value })} className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-2xl h-14" />
            </div>
            <div>
              <label className="text-xs text-white/30 mb-1 block uppercase tracking-wider">Heart Rate (BPM)</label>
              <Input type="number" placeholder="e.g., 72" value={healthForm.heart_rate} onChange={(e) => setHealthForm({ ...healthForm, heart_rate: e.target.value })} className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-2xl h-14" />
            </div>
            <Button onClick={handleUpdateHealth} className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-2xl h-14 font-semibold text-base shadow-lg shadow-red-500/20">
              Save Health Data
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Helper
function formatMonthKey(key) {
  if (!key || key === "unknown") return "Timeless";
  try {
    const [year, month] = key.split("-");
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
  } catch {
    return key;
  }
}
